/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, combineLatest, filter, Observable, Subscription } from 'rxjs';

import { MonitoringRoutingService } from '@Monitor/Core/services/monitoring-routing.service';
import { RouteType } from '../enums/route-type.enum';
import { SeriesTypeEnum } from '@Common/Reports/enums/series-type.enum';
import { MonitoringRouteModel } from '../models/monitoring-route.model';
import { IndividualReportSeriesConfig } from '@Monitor/Core/services/monitoring-dashboard-search.service';
import { MADSeriesTypesEnum } from '../enums/mad-series-types.enum';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { monitoringFiltersEnum } from '../enums/monitoring-filters.enum';
import { DataPointModel } from '@Common/Charts/models/data-point.model';
import { NeoFacetTermFieldApiEnum } from '@api/mentions/enum/neo-facet-term-field-api.enum';
import { JournodexGeographyModel } from '@Common/Journodex/models/journodex-geography.model';
import { GeographyTypesEnum } from '@api/journodex/enum/geography-types.enum';
import { ChartMetricsEnum, chartMetricsToNeoFacetTermFieldMap } from '@Common/Reports/enums/chart-metrics.enum';
import { JournodexRegionsService } from '@Common/Journodex/services/journodex-regions.service';
import { MentionsSearchResponseModel } from '@api/mentions/model/mentions-search-response.model';
import { BucketFunctionApiEnum } from '@api/mentions/enum/bucket-function-api.enum';
import { ReportSeriesFacetGeneratorEnum } from '@Common/Reports/models/report-series-facet-generator.enum';
import { NeoFacetRequestApiModel } from '@api/mentions/model/neo-facet-request-api.model';
import { JournodexSourceModel } from '@Common/Journodex/models/journodex-source.model';
import { SearchCriteriaSourcesEnum } from '@api/mentions/enum/search-criteria-sources.enum';
import { NeoFacetSortByEnum } from '@api/mentions/enum/neo-facet-sort-by.enum';
import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { EntityType } from '@api/targeting/enum/entity.type.enum';
import { topContactsNeoFacetRequest, topOutletsNeoFacetRequest } from '@Common/Reports/models/report-neo-facet-requests.model';


export type MADSeriesMap = Map<MADSeriesTypesEnum, ReportSeriesModel>;

export const MADMapChartCountryNeoFacetRequest = {
    facet: {
        termField: NeoFacetTermFieldApiEnum.COUNTRY,
        key: NeoFacetTermFieldApiEnum.COUNTRY,
        aggregateBucketRequests: [
            {
                key: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
                field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
                function: BucketFunctionApiEnum.INDIVIDUAL_SUM
            },
            {
                key: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
                field: NeoFacetTermFieldApiEnum.AD_VALUE_USD,
                function: BucketFunctionApiEnum.INDIVIDUAL_SUM
            }
        ]
    },
    identifier: ReportSeriesFacetGeneratorEnum.neoFacet
};

@Injectable()
export class MADChartsService implements OnDestroy {

    currentRoute: MonitoringRouteModel;

    private subscriptions: Subscription[] = [];
    private seriesMap: MADSeriesMap = new Map<MADSeriesTypesEnum, ReportSeriesModel>;
    protected seriesMap$: BehaviorSubject<MADSeriesMap> = new BehaviorSubject<MADSeriesMap>(null);
    private chartConfig: IndividualReportSeriesConfig[];
    protected chartConfigReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        public routingService: MonitoringRoutingService,
        public localizationService: LocalizationService,
        public journodexSearchService: JournodexRegionsService
    ) {
        this.subscriptions.push(
            combineLatest([
                this.chartConfigReady$.pipe(filter(x => !!x)),
                this.routingService.getCurrentRoute()
            ]).subscribe(([ready, route]) => {
                this.currentRoute = route;
                this.setUpSeriesMap();

                this.seriesMap.forEach((value, key) => {
                    switch (route.type) {
                        case RouteType.topicAnalyticsSyndicates:
                        case RouteType.topicAnalyticsDashboard:
                            value.type = SeriesTypeEnum.topic;
                            value.mentions.topicId = +route.value;
                            break;
                        case RouteType.folderAnalyticsSyndicates:
                        case RouteType.folderAnalyticsDashboard:
                            value.type = SeriesTypeEnum.folder;
                            value.mentions.folderId = +route.value;
                            break;
                        case RouteType.subscriptionAnalyticsSyndicates:
                        case RouteType.subscriptionAnalyticsDashboard:
                            value.type = SeriesTypeEnum.subscription;
                            value.mentions.mediaSubscriptionId = +route.value;
                            break;
                    }

                });

                this.seriesMap$.next(this.seriesMap);

            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    setChartConfig(config: IndividualReportSeriesConfig[]): void {
        this.chartConfig = config;
        this.chartConfigReady$.next(true);
    }

    setUpSeriesMap(): void {
        if (!this.chartConfig) {
            return;
        }
        this.chartConfig.forEach(config => {
            const series = new ReportSeriesModel();
            if (config.label) {
                series.label = this.localizationService.transform(config.label);
            }
            if (config.metric) {
                series.metric = config.metric;
            }
            if (config.metrics) {
                series.metrics = config.metrics;
            }
            if (config.isPreviousPeriod) {
                series.mentions.isPreviousPeriod = config.isPreviousPeriod;
            }
            if (config.mentionProvider) {
                series.mentionProvider = config.mentionProvider;
            }
            if (config.facetGenerator) {
                series.mentions.facetGenerator = config.facetGenerator;
            }
            if (config.dataMapper) {
                series.mentions.dataMapper = config.dataMapper;
            }
            if (config.asyncDataMapper) {
                series.mentions.asyncDataMapper = config.asyncDataMapper;
            }
            if (config.mentionDataMapper) {
                series.mentions.mentionDataMapper = config.mentionDataMapper;
            }
            if (config.mentionRequestGenerator) {
                series.mentions.mentionRequestGenerator = config.mentionRequestGenerator;
            }
            if (config.mentionProvider) {
                series.mentions.mentionRequestGenerator = config.mentionRequestGenerator;
            }

            this.seriesMap.set(config.type, series);
        });
    }

    generateFilterErrorMessage(error: any): string {
        if (error?.filterTypes?.some( errorType =>
            errorType === monitoringFiltersEnum.regions
            || errorType === monitoringFiltersEnum.country
            || errorType === monitoringFiltersEnum.city
            || errorType === monitoringFiltersEnum.province
        )) {
            return this.localizationService.transform('Reports.filterError.regions');
        } else {
            throw error;
        }
    }

    getLocationDataFromNeoFacets(response: MentionsSearchResponseModel, request, series: ReportSeriesModel): DataPointModel[] {
        const neoFacet = response.neoFacetResults.find(facet => facet.key === NeoFacetTermFieldApiEnum.COUNTRY);
        if (!neoFacet) {
            return [];
        }
        let total = 0;
        const data = [];
        neoFacet.buckets.forEach(bucket => {
            const country = this.journodexSearchService.getCountryById(bucket.termValue);
            if (!country) {
                return;
            }
            const geoModel = new JournodexGeographyModel();
            geoModel.geographicType = GeographyTypesEnum.country;
            geoModel.id = +country.id;

            let viewUrl;
            let filterErrorMessage;
            // HACK some region filters cannot be safely applied to coverage by region chart
            try {
                const s = series.clone();
                s.mentions.combinedQuery.includedRegions = [ geoModel ];
                viewUrl = s.getMentionSeriesUrl();
            } catch (error) {
                filterErrorMessage = this.generateFilterErrorMessage(error);
            }

            let value = 0;
            switch (series.metric) {
                case ChartMetricsEnum.volumeOfMentions:
                    value = bucket.count;
                    break;
                case ChartMetricsEnum.IMPRESSIONS:
                    value = bucket.buckets[NeoFacetTermFieldApiEnum.AUDIENCE_REACH][0]?.count || 0;
                    break;
                case ChartMetricsEnum.ave:
                    value = bucket.buckets[NeoFacetTermFieldApiEnum.AD_VALUE_USD][0]?.count || 0;
                    break;
            }
            total += value;

            data.push({
                id:  country.countryCode,
                label: country.name,
                xValue: value,
                viewUrl: viewUrl,
                filterErrorMessage: filterErrorMessage
            });
        });

        data.forEach((dataPoint) => {
            dataPoint.yValue = parseFloat((dataPoint.xValue / total * 100).toFixed(1));
        });

        return data;
    }

    getSeriesMap(): Observable<MADSeriesMap> {
        return this.seriesMap$;
    }

    topOutletsDataMapper(response: MentionsSearchResponseModel, series: ReportSeriesModel): DataPointModel[] {
        const outletFacetResults =  response.neoFacetResults.find(facet => facet.key === NeoFacetTermFieldApiEnum.OUTLET_NAME_FACET)?.buckets;
        const data: DataPointModel[] = [];
        if (outletFacetResults) {
            let totalResults = 0;
            for (const facetValue of outletFacetResults) {
                let facetCount;
                switch (series.metric) {
                    case ChartMetricsEnum.VOLUME:
                        facetCount = facetValue.count;
                        break;
                    case ChartMetricsEnum.IMPRESSIONS:
                        facetCount = facetValue.buckets[NeoFacetTermFieldApiEnum.AUDIENCE_REACH][0].count;
                        break;
                    case ChartMetricsEnum.ave:
                        facetCount = facetValue.buckets[NeoFacetTermFieldApiEnum.AD_VALUE_USD][0].count;
                        break;
                    case ChartMetricsEnum.domainAuthority:
                        facetCount = facetValue.buckets[NeoFacetTermFieldApiEnum.DOMAIN_AUTHORITY][0].count;
                        break;
                }
                if (!facetCount || facetValue.termValue === '') {
                    continue;
                }
                const dataPoint: DataPointModel = {
                    label: facetValue.termValue.replace(/[\r\n]+/g, ' '),
                    uuid: facetValue.buckets[NeoFacetTermFieldApiEnum.OUTLET_NAME_X_OUTLET_UUID][0]?.termValue,
                    mappingKeys: {},
                    value: facetCount
                };
                totalResults += facetCount;
                const s = series.clone();
                const outlet = new JournodexSourceModel();
                outlet.entityType = SearchCriteriaSourcesEnum.outlet;
                outlet.entityName = facetValue.termValue;
                s.mentions.combinedQuery.includedSources = [ outlet ];
                dataPoint.mappingKeys['outlet'] = facetValue.termValue;
                dataPoint['viewUrl'] = s.getMentionSeriesUrl();

                data.push(dataPoint);
            }
            data.forEach(x => {
                x.percent = parseFloat((x.value / totalResults * 100).toFixed(1));
            });
        }
        return data.sort((a, b) => b.value - a.value).slice(0, 10);
    }

    topAuthorsDataMapper(response: MentionsSearchResponseModel, series: ReportSeriesModel): DataPointModel[] {
        const contactFacetResults =  response.neoFacetResults.find(facet => facet.key === NeoFacetTermFieldApiEnum.CONTACT_NAME)?.buckets;
        const data: DataPointModel[] = [];
        if (contactFacetResults) {
            let totalResults = 0;
            for (const facetValue of contactFacetResults) {
                let facetCount;
                switch (series.metric) {
                    case ChartMetricsEnum.VOLUME:
                        facetCount = facetValue.count;
                        break;
                    case ChartMetricsEnum.IMPRESSIONS:
                        facetCount = facetValue.buckets[NeoFacetTermFieldApiEnum.AUDIENCE_REACH][0].count;
                        break;
                    case ChartMetricsEnum.ave:
                        facetCount = facetValue.buckets[NeoFacetTermFieldApiEnum.AD_VALUE_USD][0].count;
                        break;
                    case ChartMetricsEnum.domainAuthority:
                        facetCount = facetValue.buckets[NeoFacetTermFieldApiEnum.DOMAIN_AUTHORITY][0].count;
                        break;
                }
                if (!facetCount || facetValue.termValue === '') {
                    continue;
                }
                totalResults += facetCount;
                const dataPoint: DataPointModel = {
                    label: facetValue.termValue.replace(/[\r\n]+/g, ' '),
                    uuid: facetValue.buckets[NeoFacetTermFieldApiEnum.CONTACT_NAME_X_CONTACT_UUID][0]?.termValue,
                    mappingKeys: {},
                    value: facetCount
                };

                const s = series.clone();
                const author = new JournodexSourceModel();
                author.entityType = SearchCriteriaSourcesEnum.journalist;
                author.entityName = facetValue.termValue;
                s.mentions.combinedQuery.includedSources = [ author ];
                dataPoint.mappingKeys['author'] = facetValue.termValue;
                dataPoint['viewUrl'] = s.getMentionSeriesUrl();

                data.push(dataPoint);
            }
            data.forEach(x => {
                x.percent = parseFloat((x.value / totalResults * 100).toFixed(1));
            });
        }
        return data.sort((a, b) => b.value - a.value).slice(0, 10);
    }

    getTopEntitiesNeoFacet(metric: ChartMetricsEnum, entityType: EntityType): NeoFacetRequestApiModel {
        const facetRequest = entityType === EntityType.CONTACT ? topContactsNeoFacetRequest: topOutletsNeoFacetRequest;
        if (metric !== ChartMetricsEnum.VOLUME) {
            facetRequest.termSort = {
                aggregateBucketKey: chartMetricsToNeoFacetTermFieldMap.get(metric),
                sortBy: NeoFacetSortByEnum.aggregate,
                sortOrder: SortOrderEnum.descending
            };
        }
        return facetRequest;
    }
}
